import type { ArcadeScreen } from "../ScreenManager.js";
import type { MenuAction } from "../MenuInput.js";
import type { LeaderboardEntry, PlayerStats } from "../../auth/AuthClient.js";
import { buildLeaderboardContentHtml, buildPodiumHtml } from "../leaderboardView.js";
import { buildPlayerStatsHtml } from "../playerStatsView.js";
import { NavList, type NavItem } from "../navList.js";
import { uiSfx } from "../uiSfx.js";

/**
 * RANKINGS — global leaderboard + personal stats in one screen
 * (docs/menu-redesign-plan.md §3.8).
 *
 * No new business logic: the leaderboard fetch is AuthClient.fetchLeaderboard
 * (same call the classic LEADERS tab makes), rows render through the shared
 * leaderboardView builder (extracted from VehicleSelect), and personal stats
 * come from AuthClient.fetchStats through the shared playerStatsView builder
 * (extracted from main.ts's LOAD STATS handler). The podium strip for the
 * top 3 is presentation on top of the same entries.
 */

export interface RankingsDeps {
  fetchLeaderboard: () => Promise<LeaderboardEntry[]>;
  fetchStats: () => Promise<PlayerStats>;
  isLoggedIn: () => boolean;
  username: () => string | null;
  /** Guest pressed the sign-in chip → OPTIONS / ACCOUNT. */
  onSignIn: () => void;
  onBack: () => void;
}

export class RankingsScreen implements ArcadeScreen {
  private root: HTMLElement | null = null;
  private podiumEl: HTMLElement | null = null;
  private tableEl: HTMLElement | null = null;
  private statsBodyEl: HTMLElement | null = null;
  private statsTitleEl: HTMLElement | null = null;
  private refreshBtn: HTMLButtonElement | null = null;
  private signinEl: HTMLButtonElement | null = null;
  private readonly nav = new NavList(() => uiSfx.play("nav"));
  private active = false;
  private enterSeq = 0;

  constructor(private readonly deps: RankingsDeps) {}

  mount(root: HTMLElement): void {
    this.root = root;
    root.innerHTML = `
      <div class="rk-wrap">
        <div class="rk-head">
          <span class="rk-eyebrow">RANKINGS</span>
          <span class="rk-headrule" aria-hidden="true"></span>
          <button type="button" class="rk-refresh" title="Refresh">↻ REFRESH</button>
        </div>
        <div class="rk-cols">
          <div class="rk-board">
            <div class="rk-podium-slot-wrap"></div>
            <div class="rk-table"><div class="rk-loading">LOADING…</div></div>
          </div>
          <aside class="rk-side">
            <div class="rk-side-eyebrow">YOUR STATS</div>
            <div class="rk-side-title"></div>
            <div class="rk-side-body"></div>
            <button type="button" class="rk-signin hidden">SIGN IN TO TRACK YOUR STATS ▸</button>
          </aside>
        </div>
        <div class="rk-footer">
          <span class="rk-hints">↑↓ SELECT &nbsp;·&nbsp; ENTER / Ⓐ CONFIRM &nbsp;·&nbsp; ESC / Ⓑ BACK</span>
        </div>
      </div>`;

    this.podiumEl = root.querySelector(".rk-podium-slot-wrap");
    this.tableEl = root.querySelector(".rk-table");
    this.statsBodyEl = root.querySelector(".rk-side-body");
    this.statsTitleEl = root.querySelector(".rk-side-title");
    this.refreshBtn = root.querySelector(".rk-refresh");
    this.signinEl = root.querySelector(".rk-signin");

    this.refreshBtn?.addEventListener("click", () => {
      uiSfx.play("confirm");
      void this.loadBoard();
    });
    this.signinEl?.addEventListener("click", () => {
      uiSfx.play("confirm");
      this.deps.onSignIn();
    });
  }

  onEnter(): void {
    this.active = true;
    this.enterSeq++;
    void this.loadBoard();
    this.loadStatsPanel();
    this.rebuildNav();
  }

  onLeave(): void {
    this.active = false;
    this.nav.clearFocus();
  }

  handleInput(action: MenuAction): boolean {
    switch (action.type) {
      case "up":
        this.nav.move(-1);
        return true;
      case "down":
        this.nav.move(1);
        return true;
      case "confirm":
      case "start":
        this.nav.activate();
        return true;
      case "back":
        uiSfx.play("back");
        this.deps.onBack();
        return true;
      default:
        return false;
    }
  }

  // ── internals ───────────────────────────────────────────────────────────

  private rebuildNav(): void {
    const items: NavItem[] = [];
    if (this.refreshBtn) {
      const btn = this.refreshBtn;
      items.push({ el: btn, activate: () => btn.click() });
    }
    if (this.signinEl && !this.signinEl.classList.contains("hidden")) {
      const chip = this.signinEl;
      items.push({ el: chip, activate: () => chip.click() });
    }
    this.nav.setItems(items, false);
  }

  private async loadBoard(): Promise<void> {
    const seq = this.enterSeq;
    if (this.tableEl) this.tableEl.innerHTML = `<div class="rk-loading">LOADING…</div>`;
    if (this.podiumEl) this.podiumEl.innerHTML = "";
    try {
      const entries = await this.deps.fetchLeaderboard();
      if (!this.active || seq !== this.enterSeq) return;
      if (this.podiumEl) this.podiumEl.innerHTML = buildPodiumHtml(entries);
      if (this.tableEl) this.tableEl.innerHTML = buildLeaderboardContentHtml(entries);
    } catch {
      if (!this.active || seq !== this.enterSeq) return;
      if (this.tableEl) {
        this.tableEl.innerHTML = `<div class="rk-error">COULD NOT LOAD LEADERBOARD</div>`;
      }
    }
  }

  private loadStatsPanel(): void {
    const seq = this.enterSeq;
    const loggedIn = this.deps.isLoggedIn();
    this.signinEl?.classList.toggle("hidden", loggedIn);
    if (this.statsTitleEl) {
      this.statsTitleEl.textContent = loggedIn ? (this.deps.username() ?? "").toUpperCase() : "GUEST";
    }
    if (!loggedIn) {
      if (this.statsBodyEl) {
        this.statsBodyEl.innerHTML = `<p class="rk-guest-note">Stats are saved to your account. Guests can still play — nothing is tracked.</p>`;
      }
      this.rebuildNav();
      return;
    }
    if (this.statsBodyEl) this.statsBodyEl.innerHTML = `<div class="rk-loading">LOADING…</div>`;
    void this.deps
      .fetchStats()
      .then((stats) => {
        if (!this.active || seq !== this.enterSeq || !this.statsBodyEl) return;
        this.statsBodyEl.innerHTML = buildPlayerStatsHtml(stats);
      })
      .catch(() => {
        if (!this.active || seq !== this.enterSeq || !this.statsBodyEl) return;
        this.statsBodyEl.innerHTML = `<div class="rk-error">COULD NOT LOAD STATS</div>`;
      });
    this.rebuildNav();
  }
}
